import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, ChevronDown, ChevronUp, UserPlus } from "lucide-react";
import AssignCourseDialog from "./AssignCourseDialog";
import type { HubState, LearnerProfile, Completion, WorkspaceCourse } from "@/lib/learnerHub/types";
import { deriveStatus, getInitials } from "@/lib/learnerHub/utils";
import { unitById } from "@/lib/learnerHub/constants";

interface Props {
  state: HubState;
  setHub: (partial: Partial<HubState>) => void;
  courses?: WorkspaceCourse[];
  onUpdateLearner?: (learner: LearnerProfile) => void;
}

function sourceBadgeClass(source: LearnerProfile["source"]): string {
  switch (source) {
    case "excel": return "bg-teal-100 text-teal-800 border-teal-200";
    case "talentlms": return "bg-blue-100 text-blue-800 border-blue-200";
    case "moodle": return "bg-purple-100 text-purple-800 border-purple-200";
    case "canvas": return "bg-orange-100 text-orange-800 border-orange-200";
    default: return "bg-muted text-muted-foreground";
  }
}

function statusBadgeClass(status: Completion["status"]): string {
  switch (status) {
    case "active": return "bg-green-100 text-green-800 border-green-200";
    case "expired": return "bg-red-100 text-red-800 border-red-200";
    case "expiring_soon": return "bg-amber-100 text-amber-800 border-amber-200";
  }
}

function confidenceDotClass(confidence: string): string {
  switch (confidence) {
    case "HIGH": return "bg-green-500";
    case "MEDIUM": return "bg-amber-400";
    case "LOW": return "bg-red-400";
    default: return "bg-muted-foreground";
  }
}

export default function ProfilesTab({ state, setHub, courses, onUpdateLearner }: Props) {
  const { learners, expandedLearnerId } = state;
  const [assigningLearner, setAssigningLearner] = useState<LearnerProfile | null>(null);

  const totalProfiles = learners.length;
  const sites = new Set(learners.map((l) => l.site).filter(Boolean)).size;
  const allCompletions = learners.flatMap((l) =>
    l.completions.map((c) => ({ ...c, status: deriveStatus(c.expiryDate) }))
  );
  const activeCerts = allCompletions.filter((c) => c.status === "active").length;
  const expiredCerts = allCompletions.filter((c) => c.status === "expired").length;

  const toggle = (id: string) =>
    setHub({ expandedLearnerId: expandedLearnerId === id ? null : id });

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total profiles", value: totalProfiles, color: "text-blue-600" },
          { label: "Sites covered", value: sites, color: "text-purple-600" },
          { label: "Active certs", value: activeCerts, color: "text-green-600" },
          { label: "Expired", value: expiredCerts, color: "text-red-600" },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="pt-4 pb-4">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Learner list */}
      {learners.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          No profiles yet. Import from the Import tab.
        </div>
      ) : (
        <div className="space-y-2">
          {learners.map((learner) => {
            const isExpanded = expandedLearnerId === learner.id;
            const expiredCount = learner.completions.filter(
              (c) => deriveStatus(c.expiryDate) === "expired"
            ).length;

            return (
              <Card key={learner.id} className="overflow-hidden">
                {/* Collapsed row */}
                <button
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/40 transition-colors"
                  onClick={() => toggle(learner.id)}
                >
                  {/* Avatar */}
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">
                    {getInitials(learner.name)}
                  </div>

                  {/* Name / meta */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{learner.name}</span>
                      <span className="text-xs text-muted-foreground">{learner.employeeId}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {learner.site} · {learner.role}
                    </div>
                  </div>

                  {/* Badges + actions */}
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    <Badge variant="outline" className={`text-xs ${sourceBadgeClass(learner.source)}`}>
                      {learner.source}
                    </Badge>
                    {expiredCount > 0 && (
                      <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                        {expiredCount} expired
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {learner.completions.length} course{learner.completions.length !== 1 ? "s" : ""}
                    </span>
                    {courses !== undefined && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={(e) => { e.stopPropagation(); setAssigningLearner(learner); }}
                      >
                        <UserPlus className="h-3.5 w-3.5 mr-1" />
                        Assign
                      </Button>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {/* Expanded completions */}
                {isExpanded && (
                  <div className="border-t bg-muted/20 px-4 py-3 space-y-3">
                    {learner.completions.map((completion) => {
                      const liveStatus = deriveStatus(completion.expiryDate);
                      return (
                        <div key={completion.id} className="bg-background rounded-lg p-3 space-y-2 border">
                          {/* Heading */}
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium">{completion.normalizedCourseName}</p>
                              {completion.rawCourseName !== completion.normalizedCourseName && (
                                <p className="text-xs text-muted-foreground italic">
                                  Original: {completion.rawCourseName}
                                </p>
                              )}
                            </div>
                            <Badge
                              variant="outline"
                              className={`text-xs shrink-0 ${statusBadgeClass(liveStatus)}`}
                            >
                              {liveStatus === "expiring_soon" ? "expiring soon" : liveStatus}
                            </Badge>
                          </div>

                          {/* Meta row */}
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                            <span>Completed: {completion.completionDate}</span>
                            {completion.expiryDate && <span>Expires: {completion.expiryDate}</span>}
                            {completion.score != null && <span>Score: {completion.score}%</span>}
                            <span>{completion.issuingBody}</span>
                          </div>

                          {/* UCL unit badges */}
                          {completion.mappedUnits.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 pt-1">
                              {completion.mappedUnits.map((mu) => {
                                const unit = unitById(mu.unitId);
                                return (
                                  <div
                                    key={mu.unitId}
                                    className="flex items-center gap-1 bg-muted rounded px-2 py-0.5 text-xs"
                                    title={unit?.label}
                                  >
                                    <span
                                      className={`h-1.5 w-1.5 rounded-full ${confidenceDotClass(mu.confidence)}`}
                                    />
                                    <span className="font-mono font-medium">{mu.unitId}</span>
                                    <span className="text-muted-foreground">{mu.method.replace("_", " ")}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {assigningLearner && courses && (
        <AssignCourseDialog
          learner={assigningLearner}
          courses={courses}
          open={!!assigningLearner}
          onOpenChange={(open) => !open && setAssigningLearner(null)}
          onLearnerUpdate={(updated) => {
            onUpdateLearner?.(updated);
            setAssigningLearner(updated);
          }}
        />
      )}
    </div>
  );
}
